import React from 'react';
import { Link } from 'react-router-dom';

export function LandingLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Animated Cloud Background */}
      <div className="cloud-bg" aria-hidden="true">
        <div className="cloud-layer layer-1" />
        <div className="cloud-layer layer-2" />
        <div className="cloud-layer layer-3" />
      </div>

      <div className="landing-container">
        {children}

        <footer className="landing-footer">
          &copy; Help from Sky
          {' · '}
          <Link to="/privacy">Privacy</Link>
          {' · '}
          <a href="mailto:hello@helpfromsky.com">Contact</a>
        </footer>
      </div>

      <style>{landingStyles}</style>
    </>
  );
}

// All landing-page styles are scoped here so they don't leak into the booking app
const landingStyles = `
  :root {
    --landing-bg: #f8fafc;
    --landing-card: #ffffff;
    --landing-text: #0f172a;
    --landing-muted: #64748b;
    --landing-accent: #5DB7DE;
    --landing-border: #e2e8f0;
    --landing-radius: 16px;
  }

  .landing-container {
    max-width: 960px;
    margin: 0 auto;
    padding: 48px 20px;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    color: var(--landing-text);
    line-height: 1.5;
    backdrop-filter: saturate(1.05);
    position: relative;
    z-index: 1;
  }

  .landing-container *, .landing-container *::before, .landing-container *::after {
    box-sizing: border-box;
  }

  .landing-container a {
    color: var(--landing-muted);
    text-decoration: none;
  }
  .landing-container a:hover {
    color: var(--landing-text);
  }

  .landing-footer {
    text-align: center;
    font-size: 14px;
    color: var(--landing-muted);
    padding: 36px 0;
  }

  /* ---- Hero ---- */
  .landing-header {
    text-align: center;
    margin-bottom: 72px;
  }

  .landing-brand-logo {
    display: flex;
    justify-content: center;
    margin-bottom: 28px;
  }

  .landing-logo-img {
    height: 56px;
    max-width: 100%;
    object-fit: contain;
    filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.35));
  }

  .landing-container h1 {
    font-size: 40px;
    line-height: 1.1;
    margin: 0 0 14px 0;
  }

  .landing-container h2 {
    font-size: 28px;
    margin: 0 0 16px 0;
  }

  .landing-container h3 {
    margin: 0 0 10px 0;
    font-size: 18px;
  }

  .landing-container p {
    color: var(--landing-muted);
    font-size: 18px;
    margin: 0;
  }

  .landing-hero-sub {
    max-width: 720px;
    margin: 0 auto;
    text-align: center;
  }

  .landing-cta {
    margin-top: 28px;
  }

  .landing-button {
    display: inline-block;
    background: var(--landing-accent);
    color: #fff;
    padding: 14px 28px;
    border-radius: 999px;
    font-size: 16px;
    text-decoration: none;
    font-weight: 700;
    border: none;
    cursor: pointer;
  }

  .landing-fine {
    margin-top: 12px;
    font-size: 14px;
    color: var(--landing-muted);
  }

  /* ---- Sections ---- */
  .landing-section {
    margin-bottom: 84px;
  }

  .landing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 18px;
    margin-top: 18px;
  }

  .landing-card {
    background: var(--landing-card);
    border: 1px solid var(--landing-border);
    border-radius: var(--landing-radius);
    padding: 22px;
  }

  .landing-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 14px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid var(--landing-border);
    font-weight: 600;
    color: #334155;
  }

  /* ---- Signup box ---- */
  .landing-signup {
    background: var(--landing-card);
    border: 1px solid var(--landing-border);
    border-radius: var(--landing-radius);
    padding: 32px;
    max-width: 520px;
    margin: 0 auto;
    text-align: center;
  }

  .landing-signup form {
    margin-top: 18px;
  }

  .landing-signup input[type="email"] {
    width: 100%;
    padding: 14px;
    border-radius: 12px;
    border: 1px solid var(--landing-border);
    font-size: 16px;
    margin-bottom: 14px;
    outline: none;
  }

  .landing-signup input[type="email"]:focus {
    border-color: rgba(93, 183, 222, 0.7);
    box-shadow: 0 0 0 4px rgba(93, 183, 222, 0.18);
  }

  .landing-note {
    margin-top: 12px;
    font-size: 14px;
    color: var(--landing-muted);
  }

  .landing-muted-small {
    font-size: 15px;
    color: var(--landing-muted);
    margin-top: 6px;
  }

  /* ---- Cloud background ---- */
  .cloud-bg {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    background: linear-gradient(
      180deg,
      #e0f2fe 0%,
      #bae6fd 35%,
      #7dd3fc 70%,
      #38bdf8 100%
    );
  }

  .cloud-layer {
    position: absolute;
    width: 200%;
    height: 58%;
    left: -50%;
    pointer-events: none;
    -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
    mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
    animation: cloud-drift linear infinite;
    filter: blur(1.2px);
  }

  .cloud-layer::before,
  .cloud-layer::after {
    content: "";
    position: absolute;
    inset: 0;
    background-repeat: repeat-x;
    background-size: 980px auto;
    will-change: transform;
  }

  .cloud-layer::before {
    opacity: 1;
    transform: translateY(0px);
  }

  .cloud-layer::after {
    opacity: 0.6;
    transform: translateY(18px) scaleX(1.08);
    filter: blur(0.8px);
    mix-blend-mode: soft-light;
  }

  .cloud-layer.layer-1::before {
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 260'><g fill='%23ffffff'><circle cx='110' cy='170' r='95'/><circle cx='240' cy='145' r='125'/><circle cx='390' cy='175' r='105'/><circle cx='560' cy='150' r='140'/><circle cx='760' cy='178' r='112'/><circle cx='930' cy='150' r='145'/><circle cx='1120' cy='178' r='110'/><circle cx='1280' cy='148' r='135'/><circle cx='1460' cy='175' r='105'/><rect x='0' y='185' width='1600' height='140'/></g></svg>");
  }

  .cloud-layer.layer-1::after {
    background-size: 1120px auto;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 260'><g fill='%23ffffff'><circle cx='150' cy='185' r='78'/><circle cx='290' cy='165' r='105'/><circle cx='460' cy='195' r='82'/><circle cx='650' cy='168' r='115'/><circle cx='850' cy='198' r='86'/><circle cx='1030' cy='170' r='118'/><circle cx='1220' cy='196' r='84'/><circle cx='1410' cy='172' r='110'/><rect x='0' y='205' width='1600' height='120'/></g></svg>");
  }

  .cloud-layer.layer-2::before {
    background-size: 1120px auto;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 260'><g fill='%23ffffff'><circle cx='90' cy='195' r='70'/><circle cx='210' cy='175' r='95'/><circle cx='360' cy='205' r='78'/><circle cx='520' cy='178' r='105'/><circle cx='690' cy='208' r='82'/><circle cx='860' cy='182' r='112'/><circle cx='1040' cy='210' r='86'/><circle cx='1210' cy='182' r='110'/><circle cx='1390' cy='205' r='84'/><rect x='0' y='210' width='1600' height='120'/></g></svg>");
  }

  .cloud-layer.layer-2::after {
    background-size: 940px auto;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 260'><g fill='%23ffffff'><circle cx='130' cy='205' r='62'/><circle cx='260' cy='190' r='88'/><circle cx='410' cy='218' r='68'/><circle cx='580' cy='192' r='96'/><circle cx='760' cy='220' r='70'/><circle cx='930' cy='194' r='100'/><circle cx='1110' cy='220' r='72'/><circle cx='1280' cy='196' r='94'/><circle cx='1450' cy='214' r='66'/><rect x='0' y='220' width='1600' height='100'/></g></svg>");
  }

  .cloud-layer.layer-3 {
    filter: blur(2.4px);
  }
  .cloud-layer.layer-3::before { opacity: 0.9; }
  .cloud-layer.layer-3::after  { opacity: 0.45; }

  .cloud-layer.layer-1 { top: -6%; opacity: 0.62; animation-duration: 140s; }
  .cloud-layer.layer-2 { top: 12%; opacity: 0.42; animation-duration: 220s; }
  .cloud-layer.layer-3 { top: 30%; opacity: 0.30; animation-duration: 320s; }

  @keyframes cloud-drift {
    from { transform: translateX(0); }
    to   { transform: translateX(25%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .cloud-layer { animation: none; }
  }

  @media (max-width: 600px) {
    .landing-container h1 { font-size: 32px; }
    .landing-container { padding: 38px 16px; }
    .landing-logo-img { height: 44px; }
  }
`;
